import type { InboxAttachment, InboxReaction } from '@unipostudio/platform-constants';
import {
  AI_LEVEL_ID,
  DEFAULT_BLOCKED_INTENTS,
  type AgentPolicy,
  type AutopilotMode,
  type ConversationSla,
  type ConversationState,
  type EscalationLadder,
  type InboxConversationSummary,
  type InboxStats,
  type EscalationLevel,
  type InboxView,
  conversationKeyFor,
  customerKeyFor,
} from '@unipostudio/shared-types';
import type { InboxEvent } from '@/shared/services/inbox.service';
import type { PlatformId } from '@/shared/components/PlatformIcon';
import type { TeamMember } from '@/shared/services/team.service';

const PLATFORM_IDS: readonly string[] = [
  'instagram', 'facebook', 'twitter', 'linkedin', 'whatsapp', 'youtube', 'telegram', 'pinterest', 'tiktok', 'google_business', 'zetpin',
];

export type MessageAuthor = 'customer' | 'human' | 'ai';

export interface ThreadMessage {
  id: string;
  text: string;
  at: string;
  author: MessageAuthor;
  /** Still being sent; rendered immediately and replaced when the real reply arrives. */
  pending?: boolean;
  /**
   * The agent's reply was held at delivery (status `held`): it never reached
   * the customer. Shown muted with why, and never counted as an answer.
   */
  held?: boolean;
  /** Why it was held when the reply row itself says so (an undo marks it `cancelled`). */
  heldAs?: HeldReason;
  /** The reply was not delivered (status `failed`): shown muted, never counted as an answer. */
  failed?: boolean;
  /** The platform's error code when it gave one and the app has words for it (FAILED_REPLY_CODES). */
  failedCode?: FailedReplyCode | null;
  /** What the platform said when it refused the reply, as recorded (shown when there is no code with words). */
  failedError?: string | null;
  /** Accepted by us, not yet delivered by the platform (status `queued`): shown as "Sending…", never as sent. */
  queued?: boolean;
  /** The customer sent a photo, video or file stored on the server: the event that holds it. */
  media?: { eventId: string; mimeType: string | null };
  /** Photos, videos, shared posts, stories… the platform reported with the message. */
  attachments?: InboxAttachment[];
  /** Reactions left on the message. */
  reactions?: InboxReaction[];
}

/** Platform error codes the thread explains in words; anything else gets the generic note. */
export const FAILED_REPLY_CODES = ['131047', '131026', '131049', '131050'] as const;
export type FailedReplyCode = (typeof FAILED_REPLY_CODES)[number];

/** The known error code at the start of a reply's delivery error ("131047: …"), or null. */
export function failedReplyCode(deliveryError: string | null | undefined): FailedReplyCode | null {
  const code = /^\s*(\d+)/.exec(deliveryError ?? '')?.[1] ?? null;
  return code && (FAILED_REPLY_CODES as readonly string[]).includes(code) ? (code as FailedReplyCode) : null;
}

/** The InboxReply status of an agent reply held at delivery (worker: HELD_REPLY_STATUS). */
export const HELD_REPLY_STATUS = 'held';

/** The InboxReply status of a reply accepted but not yet delivered. */
export const QUEUED_REPLY_STATUS = 'queued';

/** Why the worker held an agent reply, as its timeline row names it (`held_<reason>`). */
export const HELD_REASONS = ['already_replied', 'blocked', 'opted_out', 'human_owned', 'human_replied', 'mode_changed', 'undone'] as const;

/** The InboxReply status of an automatic reply taken back inside its undo window: never sent. */
export const CANCELLED_REPLY_STATUS = 'cancelled';
export type HeldReason = (typeof HELD_REASONS)[number];

/**
 * The reason a held reply was held: the first `held_<reason>` timeline row
 * written at or after the reply. Null when the timeline has none (not loaded,
 * or a plan without it).
 */
export function heldReasonFor(
  message: Pick<ThreadMessage, 'at'>,
  activity: ReadonlyArray<{ detail: string | null; at: string }>,
): HeldReason | null {
  const rows = activity
    .filter((e) => typeof e.detail === 'string' && e.detail.startsWith('held_') && e.at >= message.at)
    .sort((a, b) => a.at.localeCompare(b.at));
  const reason = rows[0]?.detail?.slice('held_'.length) ?? null;
  return reason && (HELD_REASONS as readonly string[]).includes(reason) ? (reason as HeldReason) : null;
}

export interface SlaInfo {
  dueAt: string;
  minutesLeft: number;
  atRisk: boolean;
  breached: boolean;
}

export interface Conversation {
  /** `${platform}:${conversationId}` for DMs, the event id for a comment or mention. */
  key: string;
  /** The event API calls act on: the latest inbound event, else the latest event. */
  eventId: string;
  platform: PlatformId;
  /** Uppercase API enum, used for policy and ladder lookups. */
  channel: string;
  type: InboxEvent['type'];
  peer: string;
  handle: string;
  preview: string;
  lastAt: string;
  lastInboundAt: string | null;
  unread: boolean;
  starred: boolean;
  /** The customer spoke last and nobody has answered. */
  awaitingReply: boolean;
  messages: ThreadMessage[];
  state: ConversationState;
  /** null while the agent has it. */
  level: EscalationLevel | null;
  /** 0 for AI, 1 for the first human level, … */
  levelIndex: number;
  sla: SlaInfo | null;
  /** Merges the same person across channels by phone or handle (blueprint M04). */
  customerKey: string;
  /** The customer is blocked: kept out of "Needs me" and never answered by the agent. */
  blocked: boolean;
}

export type Stripe = 'ai' | 'neutral' | 'warn' | 'bad';

/** Marker stored in aiReplyIds when a reply's id is not known yet (the send endpoint returns none). */
export function aiTextMarker(text: string): string {
  return 'text:' + text.trim();
}

/**
 * Shared with the server, so a customer blocked here is blocked from the
 * agent, and a conversation the worker acts on is the one the list shows.
 */
export function conversationKey(e: Pick<InboxEvent, 'id' | 'platform' | 'conversationId'>): string {
  return conversationKeyFor(e);
}

/**
 * The mode the agent runs in on a channel, as the server decides it from the
 * per-platform rules it sends with the policy (`channelRules`): no agent on
 * LinkedIn, TikTok or Pinterest; `auto` only by an explicit per-channel
 * choice, never from the default; X and Google reviews only once consented.
 */
export function modeFor(channel: string, policy: AgentPolicy): AutopilotMode {
  const rule = policy.channelRules?.[channel];
  if (rule && !rule.agent) return 'off';
  const mode = policy.perChannel[channel] ?? (policy.defaultMode === 'off' ? 'off' : 'suggest');
  if (mode !== 'auto') return mode;
  // Another brand connected this account first and answers it by itself.
  if (policy.sharedChannels?.[channel]) return 'suggest';
  if (rule && (!rule.autoSend || (rule.consent !== null && !policy.consents?.[rule.consent]))) return 'suggest';
  return 'auto';
}

/** The policy's block list, with the blueprint default when the stored policy predates it. */
export function blockedIntentsOf(policy: AgentPolicy): string[] {
  return policy.blockedIntents ?? [...DEFAULT_BLOCKED_INTENTS];
}

/**
 * Whether the agent may send on its own (A01): Auto mode, confidence at or
 * above the threshold, the intent not on the block list, and nobody typing a
 * reply. TODO(api): the server enforces the same rule; this only decides what
 * the UI shows and what the mock would do.
 */
export function canAutoSend(input: { policy: AgentPolicy; channel: string; intent: string | null; confidence: number | null; typingLocked: boolean }): boolean {
  const { policy, channel, intent, confidence, typingLocked } = input;
  if (typingLocked || modeFor(channel, policy) !== 'auto') return false;
  if (confidence === null || confidence < policy.autoSendThreshold) return false;
  return !(intent && blockedIntentsOf(policy).includes(intent));
}

/**
 * One key per person across channels: a phone number wins (WhatsApp), then
 * a handle without its `@`, then the display name. Lower-cased so the same
 * person on Instagram and WhatsApp merges (A02 "customer merge").
 * TODO(api): the server should store the merge; this is the client stand-in.
 */
export { customerKeyFor };

/** Open conversations per assignee, for the load count in the assign popover (M03). */
export function openLoadByAssignee(list: Conversation[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of list) {
    const id = c.state.assigneeId;
    if (!id || isClosed(c) || c.state.status === 'snoozed') continue;
    out[id] = (out[id] ?? 0) + 1;
  }
  return out;
}

/**
 * Members who may take a conversation on a channel (M03 "filtered by channel
 * coverage"): a member who sits on a ladder level is kept only when one of
 * their levels covers the channel; a member on no level is always kept, since
 * an empty level falls back to the owner.
 */
export function membersForChannel(members: TeamMember[], ladder: EscalationLadder, channel: string): TeamMember[] {
  return members.filter((m) => {
    const levels = ladder.levels.filter((l) => l.memberIds.includes(m.userId));
    return levels.length === 0 || levels.some((l) => levelCovers(l, channel));
  });
}

/** A level's SLA applies to a channel when it lists none (all) or lists that one. */
export function levelCovers(level: EscalationLevel, channel: string): boolean {
  return level.channels.length === 0 || level.channels.includes(channel);
}

/**
 * A conversation's SLA as the chip shows it, from the server's tracker
 * deadline (Settings → Inbox SLA rules): the same clock that raises the
 * breach alert (IA-10). "At risk" is the last quarter of the window, at
 * least 15 minutes. Recomputed against `now` so the countdown moves.
 */
export function slaInfoFrom(sla: ConversationSla | null | undefined, now: number): SlaInfo | null {
  if (!sla) return null;
  const due = new Date(sla.dueAt).getTime();
  const leftMs = due - now;
  const breached = sla.breached || leftMs < 0;
  const riskMs = Math.max(15 * 60_000, sla.windowMinutes * 60_000 * 0.25);
  return { dueAt: sla.dueAt, minutesLeft: Math.round(leftMs / 60_000), breached, atRisk: !breached && leftMs <= riskMs };
}

/**
 * Stored state wins; anything missing is derived. A new conversation sits on
 * the agent's rung only where the agent answers by itself (Auto); in Suggest
 * or Off it waits for a person on the first human level, since in Suggest
 * the agent only drafts when somebody asks. Once we have replied and the
 * customer has not answered, an open conversation reads as "waiting on
 * customer". A resolved conversation whose customer has written since it was
 * resolved is open again.
 */
export function deriveState(input: {
  key: string;
  stored: (Partial<ConversationState> & { updatedAt?: string }) | undefined;
  channel: string;
  policy: AgentPolicy;
  ladder: EscalationLadder;
  awaitingReply: boolean;
  hasOutbound: boolean;
  now: number;
  /** The customer's latest message, to reopen a resolution it came after. */
  lastInboundAt?: string | null;
}): ConversationState {
  const { key, stored, channel, policy, ladder, awaitingReply, hasOutbound, now } = input;
  const firstLevelId = ladder.levels[0]?.id ?? AI_LEVEL_ID;
  const knownLevel = (id: string | undefined): boolean => id === AI_LEVEL_ID || ladder.levels.some((l) => l.id === id);

  let levelId = stored?.levelId && knownLevel(stored.levelId) ? stored.levelId : undefined;
  if (!levelId) levelId = modeFor(channel, policy) === 'auto' ? AI_LEVEL_ID : firstLevelId;
  const liveStatus = levelId === AI_LEVEL_ID ? 'ai_handling' : 'open';

  let status = stored?.status ?? liveStatus;
  const snoozedUntil = stored?.snoozedUntil ?? null;
  if (status === 'snoozed' && snoozedUntil && new Date(snoozedUntil).getTime() <= now) status = liveStatus;
  const resolvedAt = stored?.updatedAt ?? null;
  if (
    (status === 'resolved' || status === 'ai_resolved') &&
    awaitingReply &&
    input.lastInboundAt &&
    resolvedAt &&
    input.lastInboundAt > resolvedAt
  ) {
    status = liveStatus;
  }
  if ((status === 'open' || status === 'ai_handling') && !awaitingReply && hasOutbound) status = 'waiting_on_customer';
  if (status === 'waiting_on_customer' && awaitingReply) status = liveStatus;

  return {
    conversationKey: key,
    levelId,
    status,
    assigneeId: stored?.assigneeId ?? null,
    escalation: stored?.escalation ?? null,
    snoozedUntil: status === 'snoozed' ? snoozedUntil : null,
    sentiment: stored?.sentiment ?? null,
    intent: stored?.intent ?? null,
    language: stored?.language ?? null,
    aiReplyIds: stored?.aiReplyIds ?? [],
    autoSent: stored?.autoSent ?? null,
  };
}

/** Group raw events into conversations and attach agent state, level and SLA. */
export function buildConversations(input: {
  events: InboxEvent[];
  states: Record<string, Partial<ConversationState> & { updatedAt?: string }>;
  policy: AgentPolicy;
  ladder: EscalationLadder;
  /** Customer keys the workspace has blocked (M04). */
  blockedCustomers?: ReadonlySet<string>;
  now?: number;
}): Conversation[] {
  const { events, states, policy, ladder } = input;
  const blockedCustomers = input.blockedCustomers ?? new Set<string>();
  const now = input.now ?? Date.now();
  const groups = new Map<string, InboxEvent[]>();
  for (const e of events) {
    const key = conversationKey(e);
    const list = groups.get(key) ?? [];
    list.push(e);
    groups.set(key, list);
  }

  const out: Conversation[] = [];
  for (const [key, list] of groups) {
    const asc = [...list].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const latest = asc[asc.length - 1]!;
    const inbound = asc.filter((e) => e.direction === 'INBOUND');
    const latestInbound = inbound[inbound.length - 1] ?? null;
    const firstInbound = inbound[0] ?? latest;
    const stored = states[key];
    const aiReplyIds = new Set(stored?.aiReplyIds ?? []);
    // Until replies carry an author, the mock marks agent replies by id or by text.
    const byAi = (id: string, text: string): boolean => aiReplyIds.has(id) || aiReplyIds.has(aiTextMarker(text));

    const messages: ThreadMessage[] = [];
    const outboundTexts = new Set(asc.filter((e) => e.direction === 'OUTBOUND').map((e) => e.message));
    for (const e of asc) {
      messages.push({
        id: e.id,
        text: e.message,
        at: e.createdAt,
        author: e.direction === 'INBOUND' ? 'customer' : byAi(e.id, e.message) ? 'ai' : 'human',
        ...(e.hasMedia ? { media: { eventId: e.id, mimeType: e.mediaMimeType ?? null } } : {}),
        ...(e.attachments?.length ? { attachments: e.attachments } : {}),
        ...(e.reactions?.length ? { reactions: e.reactions } : {}),
      });
      // Replies to a comment or mention never come back as their own event.
      for (const r of e.replies ?? []) {
        if (!r.attachment && outboundTexts.has(r.text)) continue;
        messages.push({
          id: r.id,
          text: r.text,
          at: r.sentAt,
          author: byAi(r.id, r.text) ? 'ai' : 'human',
          ...(r.attachment ? { attachments: [{ kind: r.attachment.kind, url: r.attachment.url, mimeType: r.attachment.mimeType }] } : {}),
          ...(r.status === HELD_REPLY_STATUS ? { held: true } : {}),
          ...(r.status === CANCELLED_REPLY_STATUS ? { held: true, heldAs: 'undone' as const } : {}),
          ...(r.status === QUEUED_REPLY_STATUS ? { queued: true } : {}),
          ...(r.status.toLowerCase() === 'failed'
            ? { failed: true, failedCode: failedReplyCode(r.deliveryError), failedError: r.deliveryError ?? null }
            : {}),
        });
      }
    }
    messages.sort((a, b) => a.at.localeCompare(b.at));

    // A held or failed reply never reached the customer: it answers nothing.
    // A queued one is on its way, so it does count (the thread says "Sending").
    const delivered = messages.filter((m) => !m.held && !m.failed);
    const last = delivered[delivered.length - 1];
    const hasOutbound = delivered.some((m) => m.author !== 'customer');
    const awaitingReply = !!last && last.author === 'customer' && !(latestInbound?.isReplied ?? false);
    const channel = latest.platform.toUpperCase();

    const state = deriveState({
      key,
      stored,
      channel,
      policy,
      ladder,
      awaitingReply,
      hasOutbound,
      now,
      lastInboundAt: latestInbound?.createdAt ?? null,
    });
    const levelIdx = ladder.levels.findIndex((l) => l.id === state.levelId);
    const level = levelIdx >= 0 ? ladder.levels[levelIdx]! : null;
    const peer = firstInbound.fromName || firstInbound.fromHandle || '';
    const customerKey = customerKeyFor({ handle: firstInbound.fromHandle, peer, platform: channel });

    out.push({
      key,
      eventId: (latestInbound ?? latest).id,
      platform: (PLATFORM_IDS.includes(latest.platform) ? latest.platform : 'tiktok') as PlatformId,
      channel,
      type: (latestInbound ?? latest).type,
      peer,
      handle: firstInbound.fromHandle,
      preview: last?.text ?? latest.message,
      lastAt: last?.at ?? latest.createdAt,
      lastInboundAt: latestInbound?.createdAt ?? null,
      unread: inbound.some((e) => !e.isRead),
      starred: asc.some((e) => e.isStarred),
      awaitingReply,
      messages,
      state,
      level,
      levelIndex: levelIdx + 1,
      // From the server's tracker, on the list's summary (conversationsFromSummaries).
      sla: null,
      customerKey,
      blocked: blockedCustomers.has(customerKey),
    });
  }
  return out.sort((a, b) => b.lastAt.localeCompare(a.lastAt));
}

const isClosed = (c: Conversation): boolean => c.state.status === 'resolved' || c.state.status === 'ai_resolved';

/**
 * A level with no members listed belongs to the brand's owner (as the ladder
 * and the worker's notices say), so it is "mine" only for the owner when
 * nobody is assigned (IA-33). Without the owner known, nobody's.
 */
function levelIsMine(c: Conversation, meId: string | null, ownerId: string | null): boolean {
  if (!c.level || !meId) return false;
  return c.level.memberIds.length === 0 ? meId === ownerId : c.level.memberIds.includes(meId);
}

export function matchesView(c: Conversation, view: InboxView, meId: string | null, ownerId: string | null = null): boolean {
  switch (view) {
    case 'all':
      return true;
    case 'unread':
      return c.unread === true;
    case 'needs_me':
      return (
        !c.blocked &&
        c.level !== null &&
        c.state.status === 'open' &&
        c.awaitingReply &&
        (c.state.assigneeId === meId || (c.state.assigneeId === null && levelIsMine(c, meId, ownerId)))
      );
    case 'waiting_on_customer':
      return c.state.status === 'waiting_on_customer';
    case 'handled_by_ai':
      return c.level === null && c.state.status !== 'resolved';
    case 'assigned_to_me':
      return !!meId && c.state.assigneeId === meId && !isClosed(c);
    case 'escalated_to_me':
      return (
        !c.blocked &&
        c.state.escalation !== null &&
        !isClosed(c) &&
        (c.state.assigneeId === meId || (c.state.assigneeId === null && levelIsMine(c, meId, ownerId)))
      );
    case 'sla_at_risk':
      return !c.blocked && !!c.sla && (c.sla.atRisk || c.sla.breached);
    case 'snoozed':
      return c.state.status === 'snoozed';
    case 'resolved':
      return isClosed(c);
  }
}

export function countViews(list: Conversation[], meId: string | null, ownerId: string | null = null): Record<InboxView, number> {
  const counts: Record<InboxView, number> = {
    all: 0, needs_me: 0, unread: 0, waiting_on_customer: 0, handled_by_ai: 0, assigned_to_me: 0, escalated_to_me: 0, sla_at_risk: 0, snoozed: 0, resolved: 0,
  };
  for (const c of list) {
    for (const view of Object.keys(counts) as InboxView[]) {
      if (matchesView(c, view, meId, ownerId)) counts[view] += 1;
    }
  }
  return counts;
}

/**
 * Conversations from the server's list rows: each row's latest messages
 * built as before, with what only the server knows about the whole
 * conversation laid over them (its state, unread, starred, whether it waits
 * for an answer, its SLA deadline, a block). An open thread's full messages
 * (`threadEvents`) replace the row's few when they are loaded.
 */
export function conversationsFromSummaries(input: {
  summaries: ReadonlyArray<InboxConversationSummary<InboxEvent>>;
  threadKey?: string | null;
  threadEvents?: InboxEvent[];
  policy: AgentPolicy;
  ladder: EscalationLadder;
  now: number;
}): Conversation[] {
  const { summaries, threadKey = null, threadEvents = [], policy, ladder, now } = input;
  const out: Conversation[] = [];
  for (const row of summaries) {
    const events = row.key === threadKey && threadEvents.length > 0 ? mergeEvents(row.events, threadEvents) : row.events;
    const built = buildConversations({ events, states: { [row.key]: row.state }, policy, ladder, now })
      .find((c) => c.key === row.key);
    if (!built) continue;
    const levelIdx = ladder.levels.findIndex((l) => l.id === row.state.levelId);
    out.push({
      ...built,
      state: row.state,
      level: levelIdx >= 0 ? ladder.levels[levelIdx]! : null,
      levelIndex: levelIdx + 1,
      unread: row.unread,
      starred: row.starred,
      awaitingReply: row.awaitingReply,
      lastInboundAt: row.lastInboundAt,
      lastAt: row.lastAt > built.lastAt ? row.lastAt : built.lastAt,
      sla: slaInfoFrom(row.sla, now),
      blocked: row.blocked,
    });
  }
  return out;
}

function mergeEvents(a: InboxEvent[], b: InboxEvent[]): InboxEvent[] {
  const byId = new Map<string, InboxEvent>();
  for (const e of a) byId.set(e.id, e);
  for (const e of b) byId.set(e.id, e);
  return [...byId.values()];
}

export interface GroupFilters {
  channel: string | null;
  memberId: string | null;
  levelId: string | null;
  search: string;
}

export function matchesFilters(c: Conversation, f: GroupFilters): boolean {
  if (f.channel && c.channel !== f.channel) return false;
  if (f.memberId && c.state.assigneeId !== f.memberId) return false;
  if (f.levelId && c.state.levelId !== f.levelId) return false;
  const q = f.search.trim().toLowerCase();
  if (q && !c.peer.toLowerCase().includes(q) && !c.messages.some((m) => m.text.toLowerCase().includes(q))) return false;
  return true;
}

/** Apricot (--ai) = the agent has it, neutral = a human level, amber = SLA at risk, red = breached. */
export function stripeFor(c: Conversation): Stripe {
  if (c.sla?.breached) return 'bad';
  if (c.sla?.atRisk) return 'warn';
  if (c.level === null) return 'ai';
  return 'neutral';
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * The four stats over a given list of conversations, without deltas. The
 * inbox strip reads the server's (GET /inbox/conversations/stats, over every
 * conversation, with deltas); this stays for screens and tests that
 * summarise a list they already hold.
 */
export function computeStats(list: Conversation[]): InboxStats {
  const closed = list.filter(isClosed);
  const byAi = closed.filter((c) => c.state.status === 'ai_resolved').length;

  const firstResponses: number[] = [];
  for (const c of list) {
    const firstIn = c.messages.find((m) => m.author === 'customer');
    const firstOut = firstIn && c.messages.find((m) => m.author !== 'customer' && !m.pending && m.at > firstIn.at);
    if (firstIn && firstOut) firstResponses.push((new Date(firstOut.at).getTime() - new Date(firstIn.at).getTime()) / 60_000);
  }

  return {
    resolvedByAi: { value: closed.length ? byAi / closed.length : 0, delta: null },
    medianFirstResponseMinutes: { value: Math.round(median(firstResponses)), delta: null },
    openAtHumanLevels: { value: list.filter((c) => c.level !== null && !isClosed(c) && c.state.status !== 'snoozed').length, delta: null },
    slaBreachesThisWeek: { value: list.filter((c) => c.sla?.breached).length, delta: null },
  };
}

/** The level after this one, or null at the top of the ladder. */
export function nextLevel(c: Conversation, ladder: EscalationLadder): EscalationLevel | null {
  return ladder.levels[c.levelIndex] ?? null;
}
